// workspaceApps.js — the /add-app catalog rail + direct-ref install actions
// (WORKSPACE_APPS.md "Install — one flow, one write"). The workspace-shell
// twin of marketplace/functions `installWorkspaceApp` — same ref + auto-grant
// shape; keep the two in sync (tickets/workspace.md WS-APPS records-grant
// ticket tracks consolidating them).
//
// Calling convention matches moduleConsole.js: registered functions invoked
// via `el.call('fnName', …)`, so inside each `this` IS the element.

import _wsShared from '@symbo-ls/workspace-shared'
import { shellGetAiService, workspaceSettingsForWrite } from './shellSharedDelegates.js'
const buildWorkspaceModuleRef = (...args) => _wsShared.functions.buildWorkspaceModuleRef(...args)
const upsertWorkspaceApp = (...args) => _wsShared.functions.upsertWorkspaceApp(...args)
const appsWritePatch = (...args) => _wsShared.functions.appsWritePatch(...args)
const writeWorkspaceAppsWithRetry = (...args) =>
  _wsShared.functions.writeWorkspaceAppsWithRetry(...args)
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)
const workspaceSettingsPending = (...args) => _wsShared.functions.workspaceSettingsPending(...args)
import { loadWorkspaceProject, loadWorkspaceApps } from './workspaceProjects.js'
import { applyWorkspaceConfig } from './workspaceConfigs.js'

// The shell's authoritative state root, registered once at boot (app.js
// onCreate hands its `s.root`). The marketplace install/detail modals mount in
// an isolated @symbo.ls/windows layer whose element subtree does NOT reach the
// shell state root via getRoot()/state.root — so a hot-activate driven from
// there could never find the root that HeaderTabs/home bind to, and the live
// navbar update silently no-oped (server write succeeded, UI only updated on
// reload). This registry is the reliable handle reactivateWorkspaceModule uses.
let _shellRoot = null
// A `function`, never an arrow — the functions contract: `el.call` invokes a
// `functions/*` entry with the element as `this`, and a published record
// derives the scope that holds `_shellRoot` from that receiver. An arrow
// ignores the receiver, so the published write threw `Cannot set properties
// of undefined (setting '_shellRoot')` in every consumer. app.js calls the
// import directly, which works the same either way.
export function registerShellRoot(root) {
  if (root && typeof root === 'object') _shellRoot = root
}

const _appsNotify = (el, payload) => {
  try {
    el.call('openNotification', payload)
  } catch (err) {
    console.warn(
      '[workspaceApps]',
      payload && payload.title,
      (payload && payload.message) || '',
      err
    )
  }
}

// WRITE-BASE guard (spec §5), shared by every settings mutation below. Returns
// the bag to merge over, or null AFTER notifying — a caller that gets null must
// ABORT the mutation, never merge over `{}`.
//
// Aborting outright is the conservative half of a deliberate asymmetry with the
// per-key setters in featureFlags.js / voiceSettings.js, which only skip their
// optimistic mirror and still persist. Those PATCH exactly one leaf key and the
// server merges it, so there is a safe half worth keeping. The writes here are
// not like that: `workspaceApps` is a WHOLE-ARRAY write whose CAS precondition
// (`expectedApps`) is computed from this very bag, so a base of `[]` read off an
// unloaded record is precisely the stale writer that DROPS apps it never saw
// (tickets/server.md "workspaceApps: a stale writer can still DROP an entry it
// never saw"). There is no safe half of that write to keep, so the whole action
// stops and says so out loud rather than silently succeeding at the wrong thing.
const _settingsForWrite = (el, root, titleKey) => {
  const base = workspaceSettingsForWrite(root)
  if (base) return base
  _appsNotify(el, {
    title: el.call('polyglot', titleKey || 'workspace.apps.stillLoading'),
    message: el.call('polyglot', 'workspace.apps.stillLoadingBody'),
    type: 'error'
  })
  return null
}

// el.call('fetchDeclaredCollections', owner, key) is shared's own
// registration (workspaceModuleRef.js — the ONE manifest read + ref shape).
// The shell does not register a second definition under the same key.

// el.call('loadWorkspaceAppsCatalog') — fetch the system-published app catalog
// into root._wsAppsCatalog for the /add-app rail. Same source as the
// marketplace `app` kind (projects API, `workspaceModule: true` filter) — the
// projects API is the single catalog read for project-backed kinds
// (WORKSPACE_APPS.md "The single rule").
export async function loadWorkspaceAppsCatalog() {
  const el = this
  const root = el.state && el.state.root
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  if (!root || root._wsAppsCatalogLoading) return
  if (!sdk || typeof sdk.listPublicProjects !== 'function') return
  root.update({ _wsAppsCatalogLoading: true })
  try {
    // limit matches the marketplace's own app fetch (hydrateWorkspaceAppsFromSdk,
    // limit: 100) — was 12, a silent drift that capped this /add-app rail's
    // underlying catalog well below the real app count (tickets/workspace.md WS-APPS).
    const res = await sdk.listPublicProjects({ workspaceModule: true, limit: 100 })
    const rows = (Array.isArray(res) ? res : (res && res.data) || []).filter(
      (p) => p && p.metadata && p.metadata.workspaceModule === true
    )
    const apps = rows
      .map((p) => {
        const owner =
          (p.workspace && p.workspace.organization && p.workspace.organization.slug) ||
          (p.workspace && p.workspace.slug) ||
          'system'
        const key = p.key || p.slug || ''
        // The listing's icon (`metadata.store.icon.name` — the App Store
        // tile's own picture, the same name the app's launcher entry carries)
        // for the calm glyph the welcome's app rows wear; the install count
        // orders a list by how often an app is installed.
        const store = p.metadata && p.metadata.store
        const icon = store && store.icon && typeof store.icon === 'object' ? store.icon.name : ''
        const installs = Number(p.installCount)
        // The listing's tagline (`metadata.store.tagline`, { <lang>: text }) is
        // the app's ONE LINE on every shell surface — the /add-app card, the
        // search caption, the welcome's app rows — read through the shared
        // store-copy resolver (`storeItemText`), which finds it here by key.
        // Only the tagline rides on the row; the rest of the listing is the
        // App Store page's. A text with no language is the English one, as
        // the store's own listing reader takes it.
        const said = store && typeof store === 'object' ? store.tagline : null
        const tagline = {}
        if (typeof said === 'string' && said.trim()) tagline.en = said.trim()
        else if (said && typeof said === 'object' && !Array.isArray(said)) {
          for (const lang of Object.keys(said)) {
            if (typeof said[lang] === 'string' && said[lang].trim())
              tagline[lang] = said[lang].trim()
          }
        }
        return {
          id: `${owner}/${key}`,
          owner,
          key,
          name: p.name || key,
          category: (p.metadata && p.metadata.appCategory) || 'app',
          description: (p.metadata && p.metadata.description) || '',
          store: Object.keys(tagline).length ? { tagline } : null,
          icon: typeof icon === 'string' && /^[A-Za-z][A-Za-z0-9-]{0,63}$/.test(icon) ? icon : '',
          installCount: Number.isFinite(installs) && installs > 0 ? Math.floor(installs) : 0
        }
      })
      .filter((a) => a.key)
    root.update({ _wsAppsCatalog: apps, _wsAppsCatalogLoading: false, _wsAppsCatalogLoaded: true })
  } catch (err) {
    console.warn('[workspaceApps] catalog load failed:', err && err.message)
    root.update({ _wsAppsCatalog: [], _wsAppsCatalogLoading: false, _wsAppsCatalogLoaded: true })
  }
}

// el.call('installWorkspaceAppRef', { owner, key, channel }) — write the
// module ref onto the ACTIVE workspace (the server enforces workspace
// owner/admin; its role message is already human-readable) and hot-activate
// by re-running the module loader against the patched settings — no reload,
// the app's pages/widgets/nav appear immediately.
export async function installWorkspaceAppRef(refInput) {
  const el = this
  const root = el.state && el.state.root
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  const input = refInput || {}
  if (!root || !sdk || typeof sdk.updateWorkspaceSettings !== 'function') return
  if (!input.owner || !input.key) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.missingRef'),
      message: el.call('polyglot', 'workspace.apps.missingRefBody'),
      type: 'error'
    })
  }
  const ws = root.workspace || {}
  const wsId = ws._id || ws.id
  if (!wsId) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noWorkspace'),
      type: 'error'
    })
  }
  // WRITE-BASE (spec §5). Chosen over DECISION/RENDER because the bag is read
  // purely to build the next one — the CAS `baseline` below and the optimistic
  // mirror further down. Gated BEFORE `_wsAppsInstalling` is set so an abort
  // leaves no stuck spinner.
  const settingsBase = _settingsForWrite(el, root, 'workspace.apps.cantInstallYet')
  if (!settingsBase) return
  if (root._wsAppsInstalling) return
  root.update({ _wsAppsInstalling: `${input.owner}/${input.key}` })
  try {
    // Additive install: append to the plural `settings.workspaceApps` list
    // instead of overwriting the single `workspaceModule`. Installing app B no
    // longer clobbers app A. upsert dedups by owner/key so a re-install updates
    // in place. The single workspaceModule stays reserved for a full-takeover
    // client module.
    //
    // Whole-array write — send `expectedApps` (the list this write is about
    // to build from) as the CAS precondition and retry once on a stale-read
    // 409, so an in-flight install can't be the writer that drops a sibling
    // app it never saw (tickets/server.md "workspaceApps: a stale writer can
    // still DROP an entry it never saw").
    //
    // Read off `settingsBase` (the §5-guarded bag), never `ws.settings` — the
    // optional-chained read could only ever return `[]` for a record that had
    // simply not arrived, which is the exact stale baseline this CAS exists to
    // defend against.
    const baseline = settingsBase.workspaceApps || []
    const doWrite = async (apps) => {
      // Hand the EXISTING ref through as `prevRef` (a re-install / channel-or-
      // tier change on an already-installed app) so buildWorkspaceModuleRef
      // preserves any hand-granted capabilities (ai/files) that live on it and
      // that this builder has no manifest signal to re-derive — otherwise a
      // simple channel switch would silently wipe them (tickets/workspace.md
      // "a workspace app's capability grant never re-syncs").
      const prevRef = (apps || []).find(
        (r) =>
          r &&
          String(r.owner).toLowerCase() === String(input.owner).toLowerCase() &&
          String(r.key).toLowerCase() === String(input.key).toLowerCase()
      )
      const appRef = await buildWorkspaceModuleRef({ ...input, prevRef })
      const workspaceApps = upsertWorkspaceApp(apps, appRef)
      await sdk.updateWorkspaceSettings(wsId, appsWritePatch({ workspaceApps }, apps))
      return workspaceApps
    }
    const workspaceApps = await writeWorkspaceAppsWithRetry(sdk, wsId, baseline, doWrite)
    // Patch the local settings bag so loadWorkspaceApps resolves the new list
    // without a refetch, then hot-activate. Merged over the §5-guarded base,
    // so this mirror can only ever be built on a bag that really exists.
    const settings = { ...settingsBase, workspaceApps }
    root.update({ workspace: { ...ws, settings } })
    await loadWorkspaceApps(root, sdk)
    // Surface the app's configDefaults (navbar / launcher apps) into the live
    // shell — loadWorkspaceApps injects pages+components and sets
    // root.workspaceApps, but the navbar/launcher only re-render when
    // applyWorkspaceConfig re-runs (resolveWorkspaceConfig appends each app's
    // navbar). Without this the app installs + its routes exist but its nav
    // entry (e.g. crm-app's "Contacts" → /crm) never appears until a reload.
    applyWorkspaceConfig(root)
    const appId = `${String(input.owner).toLowerCase()}/${String(input.key).toLowerCase()}`
    // The app's App Store name, in the session language (Nika 2026-10-23:
    // "app names = what a person sees in the workspace App Store"): a catalog
    // card's install passes the record's English `name`, and a direct ref
    // (the /add-app owner + key fields) passes none — the notice printed
    // "system/commerce-app installed". installedAppName asks THE store
    // resolver with the record identity; an app the store ships no name for
    // reads the name it was installed by, else its own first tab, else its id.
    const loaded = (Array.isArray(root.workspaceApps) ? root.workspaceApps : []).find(
      (a) => String(a && a.appId).toLowerCase() === appId
    )
    const label =
      el.call('installedAppName', {
        appId: `${input.owner}/${input.key}`,
        label: input.name || '',
        navbar: loaded && loaded.navbar,
        apps: loaded && loaded.apps
      }) || `${input.owner}/${input.key}`
    // Auto-navigate to the freshly-installed app — same one-flow contract as
    // the marketplace install paths (reactivateWorkspaceModule +
    // navigateToWorkspaceApp), so /add-app doesn't feel like a second, lesser
    // install entry point (Fable 2026-08-09 acceptance contract). No-ops
    // silently if a landing route can't be resolved — the app is still
    // installed and live either way.
    try {
      el.call('navigateToWorkspaceApp', appId)
    } catch (_) {
      /* navigation surface unavailable — the app is still installed + live */
    }
    // Resolve the landing path again for the notification's own `href` so
    // clicking "Open" on the toast re-navigates even if the auto-navigate
    // above no-oped (e.g. fired from a context with no PageSlot yet).
    let openHref = null
    try {
      const apps = Array.isArray(root.workspaceApps) ? root.workspaceApps : []
      const app = apps.find((a) => String(a && a.appId).toLowerCase() === appId)
      openHref =
        (app && Array.isArray(app.navbar) && app.navbar.find((t) => t && t.path)?.path) || null
    } catch (_) {
      /* best-effort — the toast still confirms success without a click-through */
    }
    // Copy matches the house install-flow contract exactly: "<App> installed
    // — Open" (Open navigates via `href`, never window.location — see
    // GlobalNotification/openNotificationTarget).
    _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.installed').replace('{app}', label),
      message: el.call('polyglot', 'workspace.apps.installedBody'),
      type: 'success',
      ...(openHref ? { href: openHref } : {})
    })
  } catch (err) {
    _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.installFailed'),
      message: (err && err.message) || el.call('polyglot', 'workspace.apps.installFailedBody'),
      type: 'error'
    })
  } finally {
    root.update({ _wsAppsInstalling: null })
  }
}

// ── AI-built extension → workspace app ───────────────────────────────────────
// (2026-07-24 per Nika: after "Save" comes "Add to Workspace" — every saved
// app can be added.) Adding is a two-step promotion behind ONE confirm:
//   1. the extension is SHARED workspace-wide (ai.updateExtension scope
//      'shared') — an installed app every member can open must be readable by
//      every member; this keeps the extensionSaveShare safety gate (sharing is
//      always an explicit, human-confirmed action);
//   2. an `{ type: 'extension', projectId }` ref is upserted into the additive
//      `settings.workspaceApps` list and hot-activated — the app gets a navbar
//      tab + launcher tile, opening through the SAME isolated iframe pipeline
//      /add-app uses (workspaceProjects.js extension branch → ExtensionEmbed).

// Is this extension already referenced by the workspace's additive apps list?
// Pure read used by the Add-to-Workspace buttons for their added-state label.
//
// RENDER site (spec §5). The returned boolean is not consumed as a branch with
// a safe default — it is painted, as the literal words "Added to workspace" vs
// "Add to Workspace". `false` from an unloaded record therefore renders an
// already-added app as not added, inviting a duplicate install. The boolean
// return is kept (call sites read it as a label switch), and the missing third
// state ships as the sibling below, which those call sites must check FIRST.
export const isExtensionAddedToWorkspace = (root, projectId) => {
  const id = String(projectId || '')
  if (!id) return false
  const { settings } = shellWorkspaceSettings(root)
  const list = settings && settings.workspaceApps
  return (Array.isArray(list) ? list : []).some(
    (r) => r && r.type === 'extension' && String(r.projectId) === id
  )
}

// The bit the boolean above cannot carry: true only while the full record is
// genuinely in flight. A button gating on this paints an indeterminate label
// instead of "Add to Workspace"; once completion is known-impossible it goes
// false and the button degrades to its default rather than hanging forever.
export const isExtensionAddedPending = (root) => workspaceSettingsPending(root)

// el.call('addExtensionToWorkspace', ext?) — ext is optional: the PageSlot
// preview button passes { projectId, name }; the /add-app toolbar omits it and
// the bound builder project (root._addAppProjectId) is used.
export async function addExtensionToWorkspace(extInput) {
  const el = this
  const root = el.state && el.state.root
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  if (!root || !sdk || typeof sdk.updateWorkspaceSettings !== 'function') {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.cantAddYet'),
      type: 'error'
    })
  }
  const ws = root.workspace || {}
  const wsId = ws._id || ws.id
  if (!wsId)
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noWorkspace'),
      type: 'error'
    })
  // WRITE-BASE (spec §5) — same whole-array `workspaceApps` write as
  // installWorkspaceAppRef, so the same abort. Gated up here, BEFORE the
  // confirm dialog, for two reasons: it never asks the user to confirm an
  // action that would then refuse, and the `isExtensionAddedToWorkspace` check
  // below becomes a genuine answer rather than a `false` from an unread record
  // (which would let this path share an extension workspace-wide a second time
  // for an app that was already added).
  const settingsBase = _settingsForWrite(el, root, 'workspace.apps.cantAddYet')
  if (!settingsBase) return

  const projectId = String(
    (extInput && (extInput.projectId || extInput.id)) || root._addAppProjectId || ''
  )
  if (!projectId) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.saveFirst'),
      message: el.call('polyglot', 'workspace.apps.saveFirstBody'),
      type: 'error'
    })
  }
  const row = (root._userApps || []).find((a) => a && String(a.id) === projectId) || null
  const name =
    (extInput && extInput.name) ||
    (row && row.name) ||
    el.call('polyglot', 'workspace.apps.nameFallback')

  if (isExtensionAddedToWorkspace(root, projectId)) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.alreadyAdded').replace('{app}', name),
      type: 'info'
    })
  }

  el.call('confirmDialog', {
    title: el.call('polyglot', 'workspace.apps.addConfirm'),
    message: el.call('polyglot', 'workspace.apps.addConfirmBody').replace('{app}', name),
    confirmText: el.call('polyglot', 'workspace.apps.addCta'),
    // No glyph: the kit's confirm draws its icon at the head row's end, right
    // beside Close, where a "+" read as a second button (W16b live check).
    icon: null,
    onConfirm: async () => {
      if (root._wsAppsInstalling) {
        return _appsNotify(el, {
          title: el.call('polyglot', 'workspace.addApp.installingOwn'),
          type: 'info'
        })
      }
      root.update({ _wsAppsInstalling: `extension/${projectId}` })
      try {
        // 1) Share — skip only when the list already shows it shared.
        const ai = shellGetAiService()
        if (ai && typeof ai.updateExtension === 'function' && (!row || row.scope !== 'shared')) {
          await ai.updateExtension(projectId, { workspaceId: wsId, scope: 'shared' })
        }
        // 2) Reference it from the additive apps list + hot-activate (same
        //    sequence as installWorkspaceAppRef above). owner/key make the
        //    ref dedupe-able by the shared upsert's `owner/key` identity.
        // Whole-array write — CAS precondition + retry-once (see
        // installWorkspaceAppRef above / tickets/server.md).
        const ref = { type: 'extension', owner: 'extension', key: projectId, projectId, name }
        // Baseline off the §5-guarded bag (see installWorkspaceAppRef) — an
        // optional-chained `[]` here is indistinguishable from "this workspace
        // has no apps" and would make the CAS precondition assert a lie.
        const baseline = settingsBase.workspaceApps || []
        const workspaceApps = await writeWorkspaceAppsWithRetry(
          sdk,
          wsId,
          baseline,
          async (apps) => {
            const next = upsertWorkspaceApp(apps, ref)
            await sdk.updateWorkspaceSettings(wsId, appsWritePatch({ workspaceApps: next }, apps))
            return next
          }
        )
        const settings = { ...settingsBase, workspaceApps }
        root.update({ workspace: { ...ws, settings } })
        // A deliberate install hot-activates quietly: the activation strip's
        // "Retrying workspace loading" belongs to a boot or a retry, never to
        // the apps reload after the person's own click (BLD-28).
        await loadWorkspaceApps(root, sdk, { quiet: true })
        applyWorkspaceConfig(root)
        const added = el.call('polyglot', 'workspace.apps.added').replace('{app}', name)
        // WHERE THE APP IS NOW (functions/addAppSaved.js, BLD-28): shared and
        // in the workspace — its row in Your apps at once (the builder's list
        // shows the Shared place, where it went), the list read again, and the
        // builder that holds it says so with the way to its own page.
        const app = { id: projectId, name, scope: 'shared', added: true }
        try {
          el.call('addAppSavedRow', root, app)
          el.call('loadAddAppExtensions', root)
          if (String(root._addAppProjectId || '') === projectId) {
            el.call('addAppSavedNotice', root, added, app)
          }
        } catch (_) {
          /* the builder is not here: the notification below still says it */
        }
        _appsNotify(el, {
          title: added,
          message: el.call('polyglot', 'workspace.apps.addedBody'),
          type: 'success',
          // a press opens the app on its own page
          href: el.call('addAppSavedPath', root, projectId)
        })
      } catch (err) {
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.addFailed'),
          message: (err && err.message) || el.call('polyglot', 'workspace.apps.serverRejected'),
          type: 'error'
        })
      } finally {
        root.update({ _wsAppsInstalling: null })
      }
    }
  })
}

// el.call('reactivateWorkspaceModule', hostArg?, settingsPatch?) — re-run the
// ACTIVE workspace's module loader AND re-apply its config so a just-installed
// / just-changed module hot-activates (pages, widgets, AND navbar) WITHOUT a
// reload. The marketplace install/uninstall paths (marketplace/functions
// submitInstallModal / uninstallWorkspaceApp) call this after their OWN server
// write so they behave like the in-shell /add-app path (installWorkspaceAppRef
// above) — tickets/workspace.md WS-APPS bug #2.
//
// `settingsPatch` (e.g. `{ workspaceModule: ref }` or `{ workspaceModule: null }`)
// is merged into the resolved root's `workspace.settings` BEFORE the loader runs
// — loadWorkspaceProject resolves the ref from root.workspace.settings (never
// refetched), so a caller whose server write just changed the module MUST hand
// the new value through here or the hot-cycle is a silent no-op. Owning the
// patch here (with the resolved root) beats every caller guessing which of its
// host-shaped objects carries `workspace` — the marketplace island host carries
// none, which is exactly how the install/uninstall live-update no-oped.
//
// `opts.quiet` (R2c-m2): the reload after the person's OWN apply (the
// welcome's / App Store's preset apply, the setup job's app steps) is no
// reason for the "Retrying workspace loading" strip — the same rule as the
// /add-app install's quiet reload (BLD-28). Both loaders run quiet; a
// failure still shows (each settle writes its outcome without `quiet`).
export async function reactivateWorkspaceModule(hostArg, settingsPatch, opts) {
  const quiet = !!(opts && typeof opts === 'object' && opts.quiet === true)
  const el = this
  // Resolve the workspace-bearing root robustly. The marketplace passes its
  // island host — a state-shaped node (marketplaceHostOf/useHostState) that may
  // carry `workspace` itself, on `.root`, or on NEITHER (app-window islands own
  // an isolated state slice). So after the host chain, fall back to el's own
  // state root and finally the root ELEMENT's state — one of these is the shell
  // state root that carries `workspace`. sdk resolves off el or merged context.
  const rootEl = el && typeof el.getRoot === 'function' ? el.getRoot() : null
  const root = [
    _shellRoot, // authoritative — registered at boot, always the shell state root
    hostArg,
    hostArg && hostArg.root,
    el && el.state && el.state.root,
    rootEl && rootEl.state
  ].find((c) => c && c.workspace)
  // sdk: prefer the caller element, then the shell root's element, then the
  // module-level getSDK() — the marketplace island `el` often carries no `.sdk`.
  let sdk = el && (el.sdk || (el.context && el.context.methods && el.context.methods.sdk))
  if (!sdk && _shellRoot) {
    const sel = _shellRoot.__element || (_shellRoot.getRoot && _shellRoot.getRoot())
    sdk = (sel && sel.sdk) || (sel && sel.context && sel.context.methods && sel.context.methods.sdk)
  }
  if (!sdk) {
    // Module-level getSDK() (deferred-wrapper import at the top of this
    // file) — no separate dynamic import of a deep sdk.js subpath needed;
    // that subpath can't resolve through the platform's public importmap
    // once this package is pushed as a library (tickets/execution-
    // board.md §1 P0).
    try {
      sdk = shellGetSDK?.()
    } catch (_) {
      /* sdk module not reachable — bail below */
    }
  }
  if (!root || !root.workspace || !sdk) return
  if (settingsPatch && typeof settingsPatch === 'object') {
    // WRITE-BASE (spec §5), and the sharpest one in this file: this merge is
    // the ONLY thing that carries the caller's already-persisted server write
    // into the local record, and the loaders below read the result. Merged over
    // a missing base it publishes a bag holding nothing but `settingsPatch` —
    // so loadWorkspaceApps then reads `workspaceApps: undefined` off a record
    // that LOOKS full and unloads every app the workspace has. Aborting the
    // hot-cycle costs the caller a reload; not aborting costs them their apps.
    //
    // Scoped to the patch branch on purpose. A patch-less reactivate is a pure
    // re-run of the loaders with no base read at all, so it is NOT a
    // write-base site and must keep working while the record is in flight.
    //
    // The record is handed to the guard directly rather than as `root`: the
    // accessor prefers an `input.root` hop, and the marketplace host chain
    // resolved above can land on a state slice whose `.root` carries a
    // DIFFERENT workspace than the one this merge is about to write. Passing
    // the record keeps the guard and the mutation talking about one object.
    const settingsBase = _settingsForWrite(
      el,
      { workspace: root.workspace },
      'Can’t apply the app change yet'
    )
    if (!settingsBase) return
    root.update({
      workspace: {
        ...root.workspace,
        settings: { ...settingsBase, ...settingsPatch }
      },
      // Drop the resolved-config self-merge cache so navbar/apps/panels
      // re-resolve fresh instead of re-asserting the outgoing module's config
      // (same reset the /add-app twin and loadPrivateData's switch path do).
      workspaceSettings: null
    })
  }
  // Re-run BOTH loaders: the single takeover module AND the additive apps list.
  // A marketplace install/uninstall hands `{ workspaceApps }` through
  // settingsPatch, so the additive path is the one that actually changed; the
  // takeover reload is a cheap no-op (memoized) when its ref is unchanged.
  await loadWorkspaceProject(root, sdk, quiet ? { quiet: true } : undefined)
  await loadWorkspaceApps(root, sdk, quiet ? { quiet: true } : undefined)
  // Surface the resolved config (navbar + launcher) into the live shell — the
  // loaders inject pages+components and set root.workspaceProject/workspaceApps,
  // but the navbar/home only re-render when applyWorkspaceConfig re-runs
  // (resolveWorkspaceConfig appends each app's navbar). Without this a
  // marketplace install loads the app's routes but its nav entry (e.g. crm-app's
  // "Contacts" → /crm) never appears until a reload.
  applyWorkspaceConfig(root)
}

// el.call('navigateToWorkspaceApp', 'owner/key') — after an install, open the
// app the user just added: resolve its landing route from the freshly-loaded
// root.workspaceApps (each entry carries the app's own `navbar`) and open it in
// the shell PageSlot exactly like clicking its navbar tab. Uses the registered
// shell root so it works when driven from the marketplace's isolated
// window-layer (same reason reactivateWorkspaceModule uses _shellRoot). No-op
// (never throws) if the app or a route can't be resolved.
export function navigateToWorkspaceApp(appId) {
  const el = this
  const want = String(appId || '').toLowerCase()
  // Find whichever candidate root actually holds the just-installed app in its
  // workspaceApps. The live shell state root (el.state.root) carries the fresh
  // post-reactivate list; the registered _shellRoot can be a stale pre-update
  // clone (state updates re-clone), so DON'T trust it blindly — pick the root
  // whose list contains the target.
  const rootEl = el && typeof el.getRoot === 'function' ? el.getRoot() : null
  const candidates = [el && el.state && el.state.root, rootEl && rootEl.state, _shellRoot]
  const hasApp = (r) =>
    r &&
    Array.isArray(r.workspaceApps) &&
    r.workspaceApps.some((a) => String(a && a.appId).toLowerCase() === want)
  const root = candidates.find(hasApp) || candidates.find((r) => r && r.workspaceApps) || null
  const apps = (root && Array.isArray(root.workspaceApps) ? root.workspaceApps : []) || []
  const app = apps.find((a) => String(a && a.appId).toLowerCase() === want) || null
  const path =
    app && Array.isArray(app.navbar) && app.navbar.length
      ? app.navbar.find((t) => t && t.path)?.path
      : null
  if (!path) return
  try {
    el.call('openAppPage', path, { preferSlot: true })
  } catch (_) {
    // openAppPage not reachable from here — fall back to the router.
    try {
      el.router?.(path, el.getRoot?.())
    } catch (__) {
      /* navigation surface unavailable — the app is still installed + live */
    }
  }
}

// el.call('uninstallWorkspaceApp') — detach the active module ref after a
// confirm (settings.workspaceModule = null). The server already treats a
// null ref as "detach, don't delete" (WorkspaceController
// validateWorkspaceModuleRef: `ref === null` → `{ ok: true }`) — no server
// change needed. Content stays: uninstalling only clears the reference, so
// the app's own pages/widgets/navbar entries reappear if reinstalled.
//
// An App Store app held in this slot (`installMode: 'app'`, the legacy
// single-app install) is removed the App Store's way instead: the same
// removeInstalledWorkspaceApp below, so the store's dialog warns first and
// the atomic DELETE runs the server's dependency guard (that DELETE unsets
// the slot for such an app). The confirm + `workspaceModule: null` PATCH
// stays for a client takeover module only (no `installMode: 'app'`): it is
// not an App Store app, the DELETE answers 404 for it, and no manifest
// declares it.
export function uninstallWorkspaceApp() {
  const el = this
  const root = el.state && el.state.root
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  if (!root || !sdk || typeof sdk.updateWorkspaceSettings !== 'function') return
  const slotRef =
    root.workspace && root.workspace.settings && root.workspace.settings.workspaceModule
  if (slotRef && slotRef.installMode === 'app' && slotRef.owner && slotRef.key) {
    return el.call('removeInstalledWorkspaceApp', `${slotRef.owner}/${slotRef.key}`)
  }
  const activeId = root.workspaceProject && root.workspaceProject.id
  if (!activeId) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noAppInstalled'),
      type: 'error'
    })
  }
  const ws = root.workspace || {}
  const wsId = ws._id || ws.id
  if (!wsId) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noWorkspace'),
      type: 'error'
    })
  }
  // WRITE-BASE (spec §5). The server PATCH here is a single merge-safe key
  // (`workspaceModule: null`), but the local mirror below rebuilds the WHOLE
  // bag and then drops `workspaceSettings` so the loaders re-resolve from it —
  // so a mirror built over a missing base hands the loaders a bag containing
  // only `workspaceModule: null` and silently strips the plural apps list too.
  // Gated before the confirm so the user is never asked to approve a refusal.
  const settingsBase = _settingsForWrite(el, root, 'workspace.apps.cantRemoveYet')
  if (!settingsBase) return

  // The takeover module by its App Store name (installedAppName — see the
  // install notice above), else its `owner/key` id as before.
  const activeName = el.call('installedAppName', root.workspaceProject) || activeId

  el.call('confirmDialog', {
    title: el.call('polyglot', 'workspace.apps.removeConfirm'),
    message: el.call('polyglot', 'workspace.apps.removeConfirmBody').replace('{app}', activeName),
    confirmText: el.call('polyglot', 'workspace.apps.removeCta'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    // The house confirm (CRUD standard D1): the primary button, no icon.
    confirmTheme: 'primary',
    icon: null,
    onConfirm: async () => {
      if (root._wsAppsUninstalling) return
      root.update({ _wsAppsUninstalling: activeId })
      try {
        await sdk.updateWorkspaceSettings(wsId, { workspaceModule: null })
        root.update({
          workspace: { ...ws, settings: { ...settingsBase, workspaceModule: null } },
          // Drop the resolved-config self-merge cache too (see
          // workspaceConfigs.js applyWorkspaceConfig's precedence comment) —
          // it otherwise keeps re-asserting the outgoing module's navbar/
          // apps/panels/skin instead of letting the hot re-apply below
          // resolve them fresh. Same reset loadPrivateData.js performs at a
          // workspace switch (the framework's own deactivate/activate cycle).
          workspaceSettings: null
        })
        await loadWorkspaceProject(root, sdk)
        await loadWorkspaceApps(root, sdk)
        applyWorkspaceConfig(root)
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.removed').replace('{app}', activeName),
          message: el.call('polyglot', 'workspace.apps.removedBody'),
          type: 'success'
        })
      } catch (err) {
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.removeFailed'),
          message: (err && err.message) || el.call('polyglot', 'workspace.apps.serverRejected'),
          type: 'error'
        })
      } finally {
        root.update({ _wsAppsUninstalling: null })
      }
    }
  })
}

// el.call('removeInstalledWorkspaceApp', appId) — /add-app's "Installed apps"
// Remove: ONE app of the PLURAL `settings.workspaceApps[]` list, by its
// `owner/key` id (the shape root.workspaceApps entries carry — see
// loadWorkspaceApps). The removal is the App Store's own, so a person meets
// one removal wherever they start it. This opens the store's remove dialog
// (marketplace MarketplaceUninstallDependentsDialog, through
// ensureMarketplaceUninstallDialog — functions/marketplaceUninstallDialog.js):
// it first names every other installed app whose manifest declares this one,
// with what each would lose, and its Remove is the atomic DELETE with the
// server's dependency guard (sdk.removeWorkspaceApp, never `force`; a 409 turns
// it into the blocked dialog). Its finish hot-deactivates the app through
// reactivateWorkspaceModule above. Same detach-not-delete contract as
// uninstallWorkspaceApp: content stays, and the app's pages/widgets/navbar
// entries reappear if reinstalled.
//
// It used to write the WHOLE `workspaceApps` list through the settings PATCH
// itself, so the server's dependency check never ran and nothing warned: Store
// came out from under Scheduling's paid bookings without a word.
export async function removeInstalledWorkspaceApp(appId) {
  const el = this
  const root = el.state && el.state.root
  if (!root) return
  const id = String(appId || '').toLowerCase()
  const sep = id.indexOf('/')
  const owner = sep > 0 ? id.slice(0, sep) : ''
  const key = sep > 0 ? id.slice(sep + 1) : ''
  if (!owner || !key) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noAppSelected'),
      type: 'error'
    })
  }
  const ws = root.workspace || {}
  if (!ws._id && !ws.id) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noWorkspace'),
      type: 'error'
    })
  }
  // WRITE-BASE (spec §5). Nothing here writes a settings bag any more (the
  // DELETE pulls one entry on the server), but the finish merges the server's
  // list over this record's settings (reactivateWorkspaceModule), and that
  // merge refuses a record still in flight. Gated before the dialog, so a
  // person is never asked to approve a removal the shell cannot then show.
  if (!_settingsForWrite(el, root, 'workspace.apps.cantRemoveYet')) return
  // The takeover removal (uninstallWorkspaceApp above) rebuilds the whole
  // local bag while it runs; a removal started under it waits for it.
  if (root._wsAppsUninstalling) return

  const apps = Array.isArray(root.workspaceApps) ? root.workspaceApps : []
  const target = apps.find((a) => a && String(a.appId).toLowerCase() === id)
  // The app by its App Store name (installedAppName — see the install notice
  // above); the store's dialog and toast read the name the item carries.
  const name = el.call('installedAppName', target || { appId: id }) || id

  const dialog = await el.call('ensureMarketplaceUninstallDialog')
  if (dialog && dialog.reason === 'notReady') {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.cantRemoveYet'),
      message: el.call('polyglot', 'workspace.apps.stillLoadingBody'),
      type: 'error'
    })
  }
  const host = dialog && dialog.host
  // The store's dialog could not load (a plane that cannot fold the
  // marketplace chunk): the server's guard still decides, behind the shell's
  // own confirm. See _removeWithServerGuard.
  if (!host) return _removeWithServerGuard(el, root, id, name)
  return el.call('openAppUninstallDialog', { kind: 'app', id, owner, key, name }, host)
}

// The Remove when the App Store's dialog cannot load. A plane that cannot fold
// the marketplace chunk (the frank-published shell has no import-map entry for
// it) used to answer "Remove failed" and remove nothing. Here the removal
// still goes through the server's dependency guard: the shell's own confirm,
// then sdk.removeWorkspaceApp — the atomic DELETE, never `force`. A 409 (an
// installed app requires this one) is the blocked state: a notice that names
// each app that requires it, by its App Store name where the shell holds the
// app, else by its id, in the store's own copy (marketplace.uninstallDeps.*).
// A removal refreshes the shell through reactivateWorkspaceModule with the
// server's settings, the path the store's dialog takes.
//
// What the fallback cannot do: warn about an OPTIONAL dependent before the
// removal. The dialog reads every manifest for that; the 409 body carries ids
// and requirements only, and an optional dependent never blocks.
const _removeWithServerGuard = (el, root, id, name) => {
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  const ws = root.workspace || {}
  const wsId = ws._id || ws.id
  if (!sdk || typeof sdk.removeWorkspaceApp !== 'function') {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.removeFailed'),
      message: el.call('polyglot', 'common.action.try_again'),
      type: 'error'
    })
  }
  el.call('confirmDialog', {
    title: el.call('polyglot', 'workspace.apps.removeConfirm'),
    message: el.call('polyglot', 'workspace.apps.removeConfirmBody').replace('{app}', name),
    confirmText: el.call('polyglot', 'workspace.apps.removeCta'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    // The house confirm (CRUD standard D1): the primary button, no icon.
    confirmTheme: 'primary',
    icon: null,
    onConfirm: async () => {
      if (root._wsAppsUninstalling) return
      root.update({ _wsAppsUninstalling: id })
      try {
        try {
          await sdk.removeWorkspaceApp(wsId, id)
        } catch (err) {
          if (err && err.status === 409) {
            return _appsNotify(el, _removeBlockedNotice(el, root, name, err))
          }
          throw err
        }
        const settings = await _settingsAfterRemoval(sdk, wsId, ws, id)
        await el.call('reactivateWorkspaceModule', root, settings)
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.removed').replace('{app}', name),
          message: el.call('polyglot', 'workspace.apps.removedBody'),
          type: 'success'
        })
      } catch (err) {
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.removeFailed'),
          message: (err && err.message) || el.call('polyglot', 'workspace.apps.serverRejected'),
          type: 'error'
        })
      } finally {
        root.update({ _wsAppsUninstalling: null })
      }
    }
  })
}

// The installed apps after a removal: the server's record, read back. If that
// read fails, the record this shell holds minus the removed app — never an
// empty list, which would unload every other app.
const _settingsAfterRemoval = async (sdk, wsId, ws, id) => {
  const fresh =
    typeof sdk.getWorkspace === 'function' ? await sdk.getWorkspace(wsId).catch(() => null) : null
  if (fresh && fresh.settings) {
    return {
      workspaceApps: fresh.settings.workspaceApps || [],
      workspaceModule: fresh.settings.workspaceModule ?? null
    }
  }
  const base = ws.settings || {}
  const idOf = (r) => `${(r && r.owner) || ''}/${(r && r.key) || ''}`.toLowerCase()
  const slot = base.workspaceModule
  return {
    workspaceApps: (base.workspaceApps || []).filter((r) => idOf(r) !== id),
    workspaceModule: slot && idOf(slot) === id ? null : (slot ?? null)
  }
}

// The blocked notice of a 409: "Can't remove {name}", and the apps that
// require it (the required ones; all of them if the body marks none).
const _removeBlockedNotice = (el, root, name, err) => {
  const cause = err && err.cause
  const all = (cause && Array.isArray(cause.dependents) && cause.dependents) || []
  const required = all.filter((d) => d && d.requirement === 'required')
  const apps = Array.isArray(root.workspaceApps) ? root.workspaceApps : []
  const names = (required.length ? required : all).map((d) => {
    const did = String((d && d.id) || '').toLowerCase()
    const entry = apps.find((a) => a && String(a.appId).toLowerCase() === did)
    return el.call('installedAppName', entry || { appId: did }) || did
  })
  return {
    title: el.call('polyglot', 'marketplace.uninstallDeps.title').replace('{name}', name),
    message: _removeDependentsList(el, names),
    type: 'error',
    duration: 8000
  }
}

// A list of names in the session language ("A, B and C" / "A, B და C").
const _removeDependentsList = (el, names) => {
  try {
    return new Intl.ListFormat(el.call('getActiveLang') || undefined, {
      type: 'conjunction'
    }).format(names)
  } catch (_) {
    return new Intl.ListFormat(undefined, { type: 'conjunction' }).format(names)
  }
}

// el.call('resetWorkspaceNavbar') — drop this workspace's own navbar
// customization (settings.navbar = null) after a confirm, restoring the
// house default (Studio / Tasks / Files). A no-op visually when the active
// module's OWN manifest declares its navbar — that layer always wins over
// the Mongo personalization layer (workspaceConfigs.js resolveWorkspaceConfig
// precedence #1); use "Remove app" to drop a module's navbar entirely.
export function resetWorkspaceNavbar() {
  const el = this
  const root = el.state && el.state.root
  const sdk = el.sdk || (el.context && el.context.methods && el.context.methods.sdk)
  if (!root || !sdk || typeof sdk.updateWorkspaceSettings !== 'function') return
  const ws = root.workspace || {}
  const wsId = ws._id || ws.id
  if (!wsId) {
    return _appsNotify(el, {
      title: el.call('polyglot', 'workspace.apps.noWorkspace'),
      type: 'error'
    })
  }
  // WRITE-BASE (spec §5). Same shape as uninstallWorkspaceApp: one merge-safe
  // key on the wire (`navbar: null`), but a full-bag local mirror. Rebuilt over
  // a missing base it would republish the record carrying nothing but
  // `navbar: null` — and applyWorkspaceConfig immediately re-resolves the shell
  // from that, so a reset of the navbar would blank the apps list with it.
  const settingsBase = _settingsForWrite(el, root, 'workspace.apps.cantResetYet')
  if (!settingsBase) return

  el.call('confirmDialog', {
    title: el.call('polyglot', 'workspace.apps.resetNavbarConfirm'),
    message: el.call('polyglot', 'workspace.apps.resetNavbarConfirmBody'),
    confirmText: el.call('polyglot', 'workspace.apps.resetNavbarCta'),
    onConfirm: async () => {
      try {
        await sdk.updateWorkspaceSettings(wsId, { navbar: null })
        root.update({
          workspace: { ...ws, settings: { ...settingsBase, navbar: null } },
          // Same self-merge cache drop as uninstallWorkspaceApp above,
          // scoped to just the navbar key so apps/panels/skin are untouched.
          workspaceSettings: { ...(root.workspaceSettings || {}), navbar: null }
        })
        applyWorkspaceConfig(root)
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.navbarReset'),
          type: 'success'
        })
      } catch (err) {
        _appsNotify(el, {
          title: el.call('polyglot', 'workspace.apps.resetFailed'),
          message: (err && err.message) || el.call('polyglot', 'workspace.apps.serverRejected'),
          type: 'error'
        })
      }
    }
  })
}
